import { useRef, useState } from "react";
import { Button } from "primereact/button";
import { Divider } from "primereact/divider";
import { Tag } from "primereact/tag";
import { Avatar } from "primereact/avatar";
import { Dialog } from "primereact/dialog";
import ReactImageCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import MeaStudentResult from "../../../components/MeaStudentResult";
import { ToFormatedDateForEvents } from "../../../utils/dates";
import MeaMemberAvatar from "../../../components/MeaMemberAvatar";
import { getCorrectedImageBlob } from "../../../utils/imageOrientation";

const RegistrationCard = ({
  student,
  editRegistrationRef,
  confirmRegistration,
  unregisterRegistration,
  training,
  onUpdateStudentPhoto,
  avatarRefreshToken,
  canEditInformations,
  canEditStudentPhotos,
  onShowObservations,
}) => {
  const avatarInputRef = useRef(null);
  const imageRef = useRef(null);
  const isInformationEditingAllowed = canEditInformations !== false;
  const isStudentPhotoEditingAllowed = canEditStudentPhotos !== false;

  // Crop state
  const [showCropModal, setShowCropModal] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState(undefined);
  const [completedCrop, setCompletedCrop] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);

  const triggerAvatarPicker = () => {
    avatarInputRef.current?.click();
  };

  const handleImageLoad = (e) => {
    const { naturalWidth: width, naturalHeight: height } = e.currentTarget;
    
    // Create a square crop with 80% of the smallest dimension
    const crop = centerCrop(
      makeAspectCrop(
        {
          unit: "%",
          width: 80,
        },
        1, // aspect ratio 1:1 for square
        width,
        height
      ),
      width,
      height
    );
    
    setCrop(crop);
    console.log("Initial crop set:", crop);
  };

  const handleAvatarFileChange = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) {
      return;
    }

    if (!onUpdateStudentPhoto) {
      console.warn('onUpdateStudentPhoto callback not provided');
      return;
    }

    if (!isStudentPhotoEditingAllowed) {
      console.warn('Student photo editing is not allowed');
      return;
    }

    // Read file and show crop modal
    try {
      const correctedBlob = await getCorrectedImageBlob(file);
      const reader = new FileReader();
      reader.onload = () => {
        setImageSrc(reader.result);
        setPendingFile(new File([correctedBlob], file.name, { type: file.type || 'image/jpeg' }));
        setShowCropModal(true);
      };
      reader.readAsDataURL(correctedBlob);
    } catch (err) {
      console.error('Error reading image:', err);
      // Fallback: try with original file
      const reader = new FileReader();
      reader.onload = () => {
        setImageSrc(reader.result);
        setPendingFile(file);
        setShowCropModal(true);
      };
      reader.readAsDataURL(file);
    }

    // Allow selecting the same file again after upload.
    event.target.value = "";
  };

  const handleCropComplete = async () => {
    if (!completedCrop || !imageRef.current || !pendingFile) {
      console.warn("Crop data or image reference missing");
      return;
    }

    try {
      const image = imageRef.current;
      
      // Scale crop coordinates from displayed image to natural image size
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;
      
      const x = completedCrop.x * scaleX;
      const y = completedCrop.y * scaleY;
      const width = completedCrop.width * scaleX;
      const height = completedCrop.height * scaleY;

      const canvas = document.createElement("canvas");
      const pixelRatio = window.devicePixelRatio || 1;

      canvas.width = width * pixelRatio;
      canvas.height = height * pixelRatio;

      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Could not get 2D context");

      ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
      ctx.imageSmoothingQuality = "high";

      console.log("Crop data:", { x, y, width, height, scaleX, scaleY, naturalWidth: image.naturalWidth, displayWidth: image.width });

      ctx.drawImage(
        image,
        x,
        y,
        width,
        height,
        0,
        0,
        width,
        height
      );

      canvas.toBlob(
        (blob) => {
          if (blob && pendingFile) {
            const croppedFile = new File(
              [blob],
              pendingFile.name,
              { type: "image/jpeg" }
            );
            console.log("Cropped file created:", croppedFile.name, croppedFile.size);
            onUpdateStudentPhoto(student, croppedFile);
            setShowCropModal(false);
            setImageSrc(null);
            setPendingFile(null);
            setCrop(undefined);
            setCompletedCrop(null);
          }
        },
        "image/jpeg",
        0.95
      );
    } catch (err) {
      console.error("Error cropping image:", err);
      // Fallback: upload without cropping
      if (pendingFile) {
        console.log("Falling back to upload without crop");
        onUpdateStudentPhoto(student, pendingFile);
        setShowCropModal(false);
        setImageSrc(null);
        setPendingFile(null);
        setCrop(undefined);
        setCompletedCrop(null);
      }
    }
  };

  const getGender = () => {
    let gender = student?.gender;
    if (gender !== undefined) {
      if (gender === "G") {
        return "Fille";
      } else if (gender === "B") {
        return "Garçon";
      } else {
        return "-";
      }
    } else {
      return "-";
    }
  };

  const getStatusSeverity = (status) => {
    switch (status) {
      case "NEW": return "secondary";
      case "PREREGISTRED": return "info";
      case "ENROLLED": return "success";
      case "UNREGISTRED": return "danger";
      case "WAITING_LIST": return "warning";
      default: return "info";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "NEW": return "Nouvelle demande";
      case "PREREGISTRED": return "Pré-inscrit";
      case "ENROLLED": return "Inscription confirmée";
      case "UNREGISTRED": return "Désinscrit";
      case "WAITING_LIST": return "Liste d'attente";
      default: return "Inconnu";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "NEW": return "pi pi-user-plus";
      case "PREREGISTRED": return "pi pi-clock";
      case "ENROLLED": return "pi pi-check-circle";
      case "UNREGISTRED": return "pi pi-times-circle";
      case "WAITING_LIST": return "pi pi-hourglass";
      default: return "pi pi-question";
    }
  };

  const getStudentFullName = () => {
    return `${student?.lastname || ""} ${student?.firstname || ""}`.trim() || "-";
  };

  const getStudentAvatarUrl = () => {
    if (!student?.avatar) {
      return "/images/user.png";
    }

    const version = avatarRefreshToken || student?.avatar_updated_at || student?.updated_at;
    if (!version) {
      return student.avatar;
    }

    const separator = student.avatar.includes("?") ? "&" : "?";
    return `${student.avatar}${separator}v=${encodeURIComponent(version)}`;
  };

  const getStatusGuidance = (status) => {
    switch (status) {
      case "NEW":
        return "Demande envoyée. L'école reviendra vers vous après étude du dossier.";
      case "PREREGISTRED":
        return "Action requise: confirmez ou désinscrivez votre enfant.";
      case "ENROLLED":
        return "Inscription validée. Vérifiez les affectations et les informations de cotisation en bas de page.";
      case "UNREGISTRED":
        return "Inscription annulée. Vous pouvez modifier les informations ou refaire une demande.";
      case "WAITING_LIST":
        return "Votre enfant est en attente. Vous serez informé dès qu'une place se libère.";
      default:
        return "Statut en cours de mise à jour.";
    }
  };

  const getStatusToneClass = (status) => {
    switch (status) {
      case "ENROLLED":
        return "family-status-spotlight-success";
      case "PREREGISTRED":
        return "family-status-spotlight-info";
      case "WAITING_LIST":
        return "family-status-spotlight-warning";
      case "UNREGISTRED":
        return "family-status-spotlight-danger";
      default:
        return "family-status-spotlight-neutral";
    }
  };

  const getStatusTopLineClass = (status) => {
    switch (status) {
      case "NEW":
        return "family-registration-topline-new";
      case "PREREGISTRED":
        return "family-registration-topline-preregistered";
      case "ENROLLED":
        return "family-registration-topline-enrolled";
      case "UNREGISTRED":
        return "family-registration-topline-unregistered";
      case "WAITING_LIST":
        return "family-registration-topline-waiting";
      default:
        return "family-registration-topline-default";
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "ENROLLED":
        return "family-status-badge-success";
      case "PREREGISTRED":
        return "family-status-badge-info";
      case "WAITING_LIST":
        return "family-status-badge-warning";
      case "UNREGISTRED":
        return "family-status-badge-danger";
      default:
        return "family-status-badge-neutral";
    }
  };

  const absencesCount = student?.absences_count ?? 0;
  const retardsCount = student?.retards_count ?? 0;
  const attendanceHistory = student?.attendance_history || [];
  const formatAttendanceDate = (value) => {
    if (!value) {
      return "-";
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return "-";
    }

    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  return (
    <div className="family-registration-card bg-white border-round border-1 border-300 overflow-hidden">
      <div className={`family-registration-topline ${getStatusTopLineClass(student?.status)}`}></div>
      <div className="grid m-0 p-0" style={{minHeight: '200px'}}>
        {/* Student Info Section */}
        <div className="col-12 lg:col-5 p-3 bg-gray-50 flex flex-column family-registration-card-side" style={{minHeight: '200px'}}>
          <div className="flex flex-column align-items-center lg:align-items-start gap-2 flex-1">
            {/* Avatar and Basic Info */}
            <div className="flex flex-column align-items-center lg:flex-row lg:align-items-center gap-3 w-full">
              <div
                className={`family-student-avatar-wrapper ${isStudentPhotoEditingAllowed ? "family-student-avatar-editable" : ""}`}
                onClick={isStudentPhotoEditingAllowed ? triggerAvatarPicker : undefined}
                title={isStudentPhotoEditingAllowed ? "Changer la photo" : undefined}
              >
                <Avatar 
                  image={getStudentAvatarUrl()}
                  size="large"
                  shape="circle"
                  className="family-student-avatar"
                />
                {isStudentPhotoEditingAllowed && (
                  <div className="family-student-avatar-overlay">
                    <i className="pi pi-camera"></i>
                  </div>
                )}
              </div>
              <div className="flex flex-column align-items-center lg:align-items-start gap-1 flex-1">
                <div className="flex align-items-center gap-2 flex-wrap mb-2">
                  <h4 className="m-0 family-student-name">
                    {getStudentFullName()}
                  </h4>
                  <span className={`family-student-status-badge ${getStatusBadgeClass(student?.status)}`}>
                    <i className={`${getStatusIcon(student?.status)} family-student-status-icon`}></i>
                    {getStatusLabel(student?.status)}
                  </span>
                </div>
                <div className="family-student-meta-row">
                  {student?.member_id && (
                    <span className="family-student-meta-pill">
                      <i className="pi pi-id-card"></i>
                      {student?.member_id}
                    </span>
                  )}
                  <span className="family-student-meta-pill">
                    <i className="pi pi-calendar"></i>
                    {student?.birthday || "-"}
                  </span>
                  <span className="family-student-meta-pill">
                    <i className="pi pi-user"></i>
                    {getGender()}
                  </span>
                  {student?.authorized_back_alone !== undefined && (
                    <span className="family-student-meta-pill">
                      <i className={`pi ${student?.authorized_back_alone ? "pi-check-circle" : "pi-times-circle"}`}></i>
                      {student?.authorized_back_alone ? "Rentre seul" : "Ne rentre pas seul"}
                    </span>
                  )}
                  {student?.authorized_take_photo !== undefined && (
                    <span className="family-student-meta-pill">
                      <i className={`pi ${student?.authorized_take_photo ? "pi-check-circle" : "pi-times-circle"}`}></i>
                      {student?.authorized_take_photo ? "Photo autorisée" : "Photo non autorisée"}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <Divider className="w-full my-2" />

          {/* Action Button - At Bottom */}
          <div className="flex flex-column gap-2 w-full">
            <Button
              label="Modifier"
              icon="pi pi-pencil"
              className="w-full family-ghost-btn family-student-edit-btn"
              size="small"
              text
              raised
              disabled={!isInformationEditingAllowed}
              onClick={() => editRegistrationRef.current.show(student)}
            />
            <Button
              label="Changer la photo"
              icon="pi pi-camera"
              className="w-full family-ghost-btn"
              size="small"
              text
              raised
              disabled={!onUpdateStudentPhoto || !isStudentPhotoEditingAllowed}
              onClick={triggerAvatarPicker}
            />
            {onShowObservations && (
              <Button
                label={`Observations (${student?.observations?.length || 0})`}
                icon="pi pi-comments"
                className="w-full family-ghost-btn"
                size="small"
                text
                raised
                badge={student?.pending_observations_count > 0 ? String(student.pending_observations_count) : undefined}
                badgeClassName="p-badge-danger"
                onClick={() => onShowObservations(student)}
              />
            )}
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleAvatarFileChange}
            />
          </div>
        </div>

        {/* Status Details Section */}
        <div className="col-12 lg:col-7 p-3 family-registration-card-content" style={{minHeight: '200px'}}>
          <div className="flex flex-column gap-4 h-full">

            {(student?.status === "ENROLLED" && (student?.courses?.length ?? 0) > 0 && (student?.absences_count !== undefined || student?.retards_count !== undefined || attendanceHistory.length > 0)) && (
              <div className="flex flex-column gap-1 family-registration-block">
                <h6 className="text-sm font-bold text-700 m-0 flex align-items-center gap-2 mb-3 family-registration-block-title mea-gradient-text">
                  <i className="pi pi-chart-bar text-xs"></i>
                  Assiduité
                </h6>
                <div className="border-1 border-300 border-round overflow-hidden family-attendance-summary-wrap">
                  {attendanceHistory.length > 0 ? (
                    <table className="family-attendance-summary-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Type</th>
                          <th>Justificatif</th>
                        </tr>
                      </thead>
                      <tbody>
                        {attendanceHistory.map((entry, index) => (
                          <tr key={`${entry.date}-${entry.type}-${index}`}>
                            <td>{formatAttendanceDate(entry.date)}</td>
                            <td>
                              <Tag
                                value={entry.type === "ABSENT" ? "Absence" : "Retard"}
                                severity={entry.type === "ABSENT" ? "danger" : "warning"}
                                icon={entry.type === "ABSENT" ? "pi pi-times-circle" : "pi pi-clock"}
                                className="text-xs"
                              />
                            </td>
                            <td>{entry.justification || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="p-3 text-sm text-600">Aucune absence ni retard enregistré.</div>
                  )}
                </div>
              </div>
            )}

            {/* Registration Information Category */}
            {(training?.registration_show_registration_info || student?.status === "NEW" || student?.status === "UNREGISTRED") && (
              <div className="flex flex-column gap-1 family-registration-block">
                <h6 className="text-sm font-bold text-700 m-0 flex align-items-center gap-2 mb-3 family-registration-block-title mea-gradient-text">
                  <i className="pi pi-info-circle text-xs"></i>
                  Informations d'inscription
                </h6>
                <div className="grid gap-2">
                  {student?.status === "NEW" && (
                    <>
                      <div className="col-6">
                        <div className="flex flex-column gap-0">
                          <span className="text-600 text-xs font-medium">Demande faite le</span>
                          <span className="text-900 font-semibold text-sm">
                            {ToFormatedDateForEvents(new Date(student?.created_at), false)}
                          </span>
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="flex flex-column gap-0">
                          <span className="text-600 text-xs font-medium">Demandé par</span>
                          <div className="flex align-items-center gap-1">
                            <MeaMemberAvatar member={student?.created_by_member} noNavigation={true} />
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                  {student?.status === "ENROLLED" && (
                    <>
                      <div className="col-6">
                        <div className="flex flex-column gap-0">
                          <span className="text-600 text-xs font-medium">Confirmé le</span>
                          <span className="text-900 font-semibold text-sm">
                            {ToFormatedDateForEvents(new Date(student?.registration_confirmation_date), false)}
                          </span>
                        </div>
                      </div>
                      {(student?.registration_confirmation_by_id === student?.father_id || student?.registration_confirmation_by_id === student?.mother_id) && (
                        <div className="col-6">
                          <div className="flex flex-column gap-0">
                            <span className="text-600 text-xs font-medium">Confirmé par</span>
                            <div className="flex align-items-center gap-1">
                              <MeaMemberAvatar member={student?.registration_confirmation_by} noNavigation={true} />
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  {student?.status === "UNREGISTRED" && (
                    <>
                      <div className="col-6">
                        <div className="flex flex-column gap-0">
                          <span className="text-600 text-xs font-medium">Désinscrit le</span>
                          <span className="text-900 font-semibold text-sm">
                            {ToFormatedDateForEvents(new Date(student?.unregistration_date), false)}
                          </span>
                        </div>
                      </div>
                      {(student?.unregistration_by_id === student?.father_id || student?.unregistration_by_id === student?.mother_id) && (
                        <div className="col-6">
                          <div className="flex flex-column gap-0">
                            <span className="text-600 text-xs font-medium">Désinscrit par</span>
                            <div className="flex align-items-center gap-1">
                              <MeaMemberAvatar member={student?.unregistration_by} noNavigation={true} />
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  {student?.status === "WAITING_LIST" && (
                    <div className="col-12">
                      <div className="flex flex-column gap-0">
                        <span className="text-600 text-xs font-medium">En attente depuis le</span>
                        <span className="text-900 font-semibold text-sm">
                          {ToFormatedDateForEvents(new Date(student?.on_waiting_list_since_date), false)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Messages Category */}
            {(training?.registration_show_messages || student?.status === "NEW" || student?.status === "UNREGISTRED") && (
              <div className="flex flex-column gap-1 family-registration-block">
                <h6 className="text-sm font-bold text-700 m-0 flex align-items-center gap-2 mb-3 family-registration-block-title mea-gradient-text">
                  <i className="pi pi-comments text-xs"></i>
                  Messages
                </h6>
                <div className="grid gap-2">
                  {student?.status === "NEW" && (
                    <>
                      <div className="col-12">
                        <div className="flex flex-column gap-0">
                          <span className="text-600 text-xs font-medium">Message du parents</span>
                          <div className="p-2 bg-primary-50 border-round border-1 border-primary-200 family-message-panel">
                            <span className="text-900 font-medium text-sm">
                              {student?.registration_details || "Aucun message"}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="col-12">
                        <div className="flex flex-column gap-0">
                          <span className="text-600 text-xs font-medium">Message de l'école</span>
                          <div className="p-2 bg-primary-50 border-round border-1 border-primary-200 family-message-panel">
                            <span className="text-900 font-medium text-sm">
                              {student?.registration_feedback || "Aucun message"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                  {student?.status === "UNREGISTRED" && (
                    <div className="col-12">
                      <div className="flex flex-column gap-0">
                        <span className="text-600 text-xs font-medium">Motif de désinscription</span>
                        <div className="p-2 bg-red-50 border-round border-1 border-red-200 family-message-panel">
                          <span className="text-900 font-medium text-sm">
                            {student?.unregistration_reason || "Aucun motif"}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                  {student?.status === "WAITING_LIST" && (
                    <div className="col-12">
                      <div className="flex flex-column gap-0">
                        <span className="text-600 text-xs font-medium">Message école</span>
                        <div className="p-2 bg-yellow-50 border-round border-1 border-yellow-200 family-message-panel">
                          <span className="text-900 font-medium text-sm">
                            {student?.on_waiting_list_details || "Aucun message"}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
           {/* Actions Category */}
           {(student?.status === "PREREGISTRED" ) && (
               <div className="flex flex-column gap-1 family-registration-block">
                 <h6 className="text-sm font-bold text-700 m-0 flex align-items-center gap-2 mb-3 family-registration-block-title mea-gradient-text">
                   <i className="pi pi-cog text-xs"></i>
                   Actions
                 </h6>
                 <div className="p-3 bg-blue-50 border-round border-1 border-blue-200 mb-3 family-action-intro">
                   <p className="text-sm text-700 m-0">
                     Votre enfant est pré-inscrit dans cette formation. Voulez-vous confirmer son inscription ? 
                     Cliquez sur <strong>"Confirmer"</strong> pour valider ou <strong>"Désinscrire"</strong> pour désinscrire votre enfant.
                   </p>
                 </div>
                 <div className="flex flex-column gap-2">
                   <Button 
                     label="Confirmer" 
                     icon="pi pi-check"
                     severity="success"
                     className="w-full family-primary-btn family-confirm-btn"
                     onClick={(e) => confirmRegistration(e, student)}
                   />
                   <Button 
                     label="Désinscrire" 
                     icon="pi pi-times"
                     severity="danger"
                     size="small"
                     outlined
                     className="w-full family-danger-outline"
                     onClick={(e) => unregisterRegistration(e, student)}
                   />
                 </div>
               </div>
             )}
            {/* Assignment Category */}
            {student?.status === "ENROLLED" && training?.registration_show_new_affectation &&(
              <div className="flex flex-column gap-1 family-registration-block">
                <h6 className="text-sm font-bold text-700 m-0 flex align-items-center gap-2 mb-3 family-registration-block-title mea-gradient-text">
                  <i className="pi pi-sitemap text-xs"></i>
                  Affectation
                </h6>
                  <div className="flex flex-column lg:flex-row gap-2 family-assignment-grid">
                    <div className="flex-1">
                      <div className="p-2 bg-blue-50 border-round border-1 border-blue-200 family-assignment-card">
                        <div className="flex align-items-center gap-1 mb-1">
                          <i className="pi pi-graduation-cap text-blue-600 text-xs"></i>
                          <span className="text-600 text-xs font-medium">Niveau</span>
                        </div>
                        <span className="text-900 font-bold text-sm">{student?.level_name || "-"}</span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="p-2 bg-green-50 border-round border-1 border-green-200 family-assignment-card">
                        <div className="flex align-items-center gap-1 mb-1">
                          <i className="pi pi-users text-green-600 text-xs"></i>
                          <span className="text-600 text-xs font-medium">Classe</span>
                        </div>
                        <span className="text-900 font-bold text-sm">{student?.class_name || "-"}</span>
                      </div>
                    </div>
                  </div>
              </div>
            )}

            {/* Courses Category for ENROLLED students */}
            {student?.status === "ENROLLED" && training?.registration_show_new_affectation && (
              <div className="flex flex-column gap-1 family-registration-block">                
                
                {student?.courses && student?.courses?.length > 0 ? (
                  <div className="border-1 border-300 border-round overflow-hidden family-courses-table">
                    <div className="bg-gray-100 p-2 border-bottom-1 border-300 family-courses-head">
                      <div className="grid text-center">
                        
                        <div className="col-4">
                          <span className="text-600 text-xs font-semibold">Créneau</span>
                        </div>
                        <div className="col-4">
                          <span className="text-600 text-xs font-semibold">Enseignant(e)</span>
                        </div>
                        <div className="col-4">
                          <span className="text-600 text-xs font-semibold">Salle</span>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white">
                      {student?.courses?.map((course, index) => (
                        <div key={index} className={`p-2 border-bottom-1 border-300 hover:bg-blue-50 transition-colors transition-duration-150 family-course-row ${index === student?.courses?.length - 1 ? 'border-bottom-0' : ''}`}>
                          <div className="grid text-center align-items-center">
                            
                            <div className="col-4">
                              <div className="flex align-items-center justify-content-center gap-1">
                                <i className="pi pi-clock text-600 text-xs"></i>
                                <span className="text-900 text-sm">{course?.slot_name || "-"}</span>
                              </div>
                            </div>
                            <div className="col-4">
                              <div className="flex align-items-center justify-content-center gap-1">
                                <i className="pi pi-user text-600 text-xs"></i>
                                <span className="text-900 text-sm">{course?.teacher || "-"}</span>
                              </div>
                            </div>
                            <div className="col-4">
                              <div className="flex align-items-center justify-content-center gap-1">
                                <i className="pi pi-map-marker text-600 text-xs"></i>
                                <span className="text-900 text-sm">{course?.classroom || "-"}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4 bg-gray-50 border-round border-1 border-300">
                    <i className="pi pi-calendar-times text-3xl text-400 mb-2"></i>
                    <div className="text-500 text-sm font-bold">Aucun cours assigné</div>
                    <div className="text-400 text-xs mt-1">Les cours seront bientôt disponibles</div>
                  </div>
                )}
              </div>
            )}

            {/* Academic History Category */}
            <div className="flex flex-column gap-1 family-registration-block">
              <h6 className="text-sm font-bold text-700 m-0 flex align-items-center gap-2 mb-3 family-registration-block-title mea-gradient-text">
                <i className="pi pi-book text-xs"></i>
                Historique
              </h6>
              <div className="grid gap-1">
                <div className="col-12">
                  <div className="flex flex-column gap-0">
                    <span className="text-600 text-xs font-medium">Dernière classe</span>
                    <span className="text-900 font-semibold text-sm">
                      {student?.previous_class_name || "-"} - {student?.previous_level_name || "-"}
                    </span>
                  </div>
                </div>
                {training?.registration_show_last_result && (
                  <>
                    <div className="col-6">
                      <div className="flex flex-column gap-0">
                        <span className="text-600 text-xs font-medium">Résultat</span>
                        <div className="flex align-items-center gap-1">
                          <MeaStudentResult student={student} previous={true} />
                        </div>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="flex flex-column gap-0">
                        <span className="text-600 text-xs font-medium">Appréciation générale</span>
                        <div className="p-2 bg-gray-50 border-round border-1 border-gray-200 family-message-panel">
                          <span className="text-900 font-medium text-sm">
                            {student?.previous_result_observation || "-"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            
          </div>
        </div>
      </div>

      {/* Image Crop Modal */}
      <Dialog
        visible={showCropModal}
        onHide={() => {
          setShowCropModal(false);
          setImageSrc(null);
          setPendingFile(null);
          setCrop(undefined);
          setCompletedCrop(null);
        }}
        header={null}
        modal
        maximized={true}
        className="image-crop-modal-fullscreen"
        contentClassName="image-crop-modal-content"
      >
        <div className="image-crop-fullscreen-wrapper">
          {imageSrc && (
            <div className="image-crop-viewport">
              <div className="image-crop-container">
                <ReactImageCrop
                  crop={crop}
                  onChange={(c) => setCrop(c)}
                  onComplete={(c) => setCompletedCrop(c)}
                  aspect={1}
                  circularCrop={false}
                  minWidth={50}
                  minHeight={50}
                >
                  <img
                    ref={imageRef}
                    src={imageSrc}
                    alt="Crop preview"
                    onLoad={handleImageLoad}
                    style={{ width: "100%", height: "auto", display: "block" }}
                  />
                </ReactImageCrop>
              </div>
              <div className="crop-info-text">
                Déplacez et redimensionnez le carré pour sélectionner la zone à conserver
              </div>
            </div>
          )}
          <div className="image-crop-footer">
            <div className="flex gap-2 justify-content-end">
              <Button
                label="Annuler"
                icon="pi pi-times"
                outlined
                onClick={() => {
                  setShowCropModal(false);
                  setImageSrc(null);
                  setPendingFile(null);
                  setCrop(undefined);
                  setCompletedCrop(null);
                }}
              />
              <Button
                label="Valider et télécharger"
                icon="pi pi-check"
                onClick={handleCropComplete}
              />
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default RegistrationCard;